import { OptionsApi } from '@core/swagger-client';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { UsagePanel } from './UsagePanel';

/**
 * Billing is shown apart from the usage figures on purpose (spec §7). It carries every input to
 * the formula — duration, peak viewers, blocks — because the figure has to be checkable line by
 * line rather than trusted as a total, and that is the reason this page lives in the tenant at all.
 */
export const BillingPanel = ({ from, to }: { from: string; to: string }) => {
    const { t } = useTranslation();
    const { data: billing, isLoading, error } = useQuery(OptionsApi.usageStatistics.billing(from, to));

    const formatDateTime = (value: string) => DateTime.fromISO(value).toLocal().toFormat('dd-LL-yyyy HH:mm');

    // The heading is outside the panel so the block announces itself while it loads rather than
    // popping in — this is the figure with a financial consequence, and it must not simply appear
    // once the rest of the page has settled.
    return (
        <section className="my-8 border-t pt-6">
            <h2 className="text-xl font-semibold">{t('UsageStatistics.Billing.Heading')}</h2>
            <UsagePanel isLoading={isLoading} error={error}>
                {billing && (
                    <>
                        <p className="text-3xl my-2">{billing.billableHours.toFixed(1)}</p>

                        {/* A range can span a block-size change, and then a single number would be
                            unreconstructable. Show which value applied to which days. */}
                        {billing.blockSizes.map((period) => (
                            <p key={period.fromDay} className="text-sm opacity-70">
                                {t('UsageStatistics.Billing.BlockSize', { size: period.viewerBlockSize })}
                                {billing.blockSizes.length > 1 && ` (${period.fromDay} – ${period.toDay})`}
                            </p>
                        ))}

                        {/* Only shown where an allowance was actually agreed. Unset is not zero,
                            and an environment that never agreed a number must not be shown as
                            exceeding one. */}
                        {billing.contractedHoursPerMonth !== null && (
                            <>
                                <p className="text-sm opacity-70">{t('UsageStatistics.Billing.Allowance', { hours: billing.contractedHoursPerMonth })}</p>
                                <p className="text-sm opacity-70">{t('UsageStatistics.Billing.MonthsOver', { count: billing.monthsOverAllowance })}</p>
                                <table className="w-full my-4 text-sm">
                                    <tbody>
                                        {billing.lastTwelveMonths.map((month) => (
                                            <tr key={month.month} className={month.overAllowance ? 'font-semibold' : undefined}>
                                                <td>{month.month}</td>
                                                <td className="text-right">{month.billableHours.toFixed(1)}</td>
                                                <td className="text-right opacity-70">{month.allowanceHours ?? '–'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        <table className="w-full my-4 text-sm">
                            <thead>
                                <tr className="text-left">
                                    <th>{t('UsageStatistics.Billing.Webinar')}</th>
                                    <th className="text-right">{t('UsageStatistics.Billing.PeakViewers')}</th>
                                    <th className="text-right">{t('UsageStatistics.Billing.Blocks')}</th>
                                    <th className="text-right">{t('UsageStatistics.Billing.BillableMinutes')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {billing.lines.map((line) => (
                                    <tr key={`${line.webinarId}-${line.startedOn}`}>
                                        <td>
                                            {line.title}
                                            <span className="block text-xs opacity-70">
                                                {formatDateTime(line.startedOn)} · {Math.round(line.durationMinutes)} min
                                            </span>
                                        </td>
                                        <td className="text-right">{line.peakViewers.toLocaleString()}</td>
                                        <td className="text-right">{line.blocks}</td>
                                        <td className="text-right">{Math.round(line.billableMinutes).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </UsagePanel>
        </section>
    );
};
